import React, { useCallback, useEffect, useMemo, useState } from "react";
import { publicLink } from "@/lib/landerDomain";
import { toast } from "sonner";
import api from "@/lib/api";
import { Eye, CursorClick, Phone, PaperPlaneTilt, Users, ChartLineUp, ArrowsClockwise, MagnifyingGlass, X } from "@phosphor-icons/react";
import { fmtDate } from "@/lib/constants";

/**
 * Iter-67 — Live LP analytics admin dashboard.
 *
 * GA-style engagement view for every live landing page:
 *   - Total impressions, unique sessions
 *   - CTA primary + phone clicks
 *   - Form start / submit-attempt / submit-success (SERVER-side stamp)
 *   - Conversion rate = form_submit_success / impressions
 *
 * Filters: days (7/30/90), kind (all / vertical / custom), slug search.
 * Access: `landing_pages.view_performance` (Media Buyers hold this by
 * default). Non-permitted actors see the 403 banner without a table.
 */
const DAY_OPTIONS = [
  { value: 7,  label: "Last 7 days" },
  { value: 30, label: "Last 30 days" },
  { value: 90, label: "Last 90 days" },
];

const KIND_OPTIONS = [
  { value: "all",       label: "All pages" },
  { value: "vertical",  label: "Standard verticals" },
  { value: "custom",    label: "Custom pages" },
];

function pct(v) {
  const n = Number(v || 0);
  return `${(n * 100).toFixed(1)}%`;
}
function num(v) {
  return Number(v || 0).toLocaleString("en-US");
}

function Kpi({ label, value, sub, icon: Icon, testId }) {
  return (
    <div className="brut-card p-4" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div className="label-mono">{label}</div>
        {Icon && <Icon size={14} className="text-zinc-400" />}
      </div>
      <div className="font-chivo text-3xl font-black leading-none mt-1">{value}</div>
      {sub && <div className="text-[10px] text-zinc-500 mt-1 uppercase tracking-widest">{sub}</div>}
    </div>
  );
}

function Sparkline({ series, testId }) {
  if (!series || series.length === 0) {
    return <span className="text-[10px] text-zinc-400" data-testid={testId}>—</span>;
  }
  const values = series.map((d) => Number(d.impressions || 0));
  const max = Math.max(1, ...values);
  const w = 90, h = 24;
  const step = series.length > 1 ? w / (series.length - 1) : 0;
  const pts = values.map((v, i) => `${(i * step).toFixed(1)},${(h - (v / max) * h).toFixed(1)}`).join(" ");
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} data-testid={testId}>
      <polyline
        fill="none"
        stroke="black"
        strokeWidth="1.4"
        points={pts}
      />
    </svg>
  );
}

export default function AdminLpLiveAnalytics() {
  const [days, setDays] = useState(30);
  const [kind, setKind] = useState("all");
  const [slugQ, setSlugQ] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setForbidden(false);
    try {
      const params = new URLSearchParams();
      params.set("days", String(days));
      if (kind !== "all") params.set("kind", kind);
      if (slugQ.trim()) params.set("slug", slugQ.trim());
      const { data: res } = await api.get(`/admin/lp-analytics/live?${params.toString()}`);
      setData(res);
    } catch (err) {
      if (err.response?.status === 403) {
        setForbidden(true);
        setData(null);
      } else {
        toast.error(err.response?.data?.detail || "Failed to load live LP analytics");
      }
    } finally { setLoading(false); }
  }, [days, kind, slugQ]);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => data?.by_page || [], [data]);
  const totals = data?.totals || null;

  if (forbidden) {
    return (
      <div className="brut-card p-8 text-center label-mono" data-testid="lp-analytics-forbidden">
        You do not have permission to view landing-page performance.
      </div>
    );
  }

  return (
    <div data-testid="admin-lp-live-analytics">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="font-chivo font-black text-3xl leading-none uppercase tracking-tight">
            Live LP analytics
          </div>
          <div className="label-mono mt-1">
            First-party impression + funnel counters, updated in real time
          </div>
        </div>
        <button
          onClick={load}
          data-testid="lp-analytics-refresh"
          className="border border-black px-3 py-1.5 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white inline-flex items-center gap-1"
        >
          <ArrowsClockwise size={11} weight="bold" />
          Refresh
        </button>
      </div>

      {/* Filters ---------------------------------------------------------- */}
      <div className="brut-card p-3 mb-4 flex flex-wrap items-center gap-3" data-testid="lp-analytics-filters">
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          data-testid="lp-analytics-days"
          className="border border-black px-2 py-1 text-sm"
        >
          {DAY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select
          value={kind}
          onChange={(e) => setKind(e.target.value)}
          data-testid="lp-analytics-kind"
          className="border border-black px-2 py-1 text-sm"
        >
          {KIND_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            type="text"
            value={slugQ}
            onChange={(e) => setSlugQ(e.target.value)}
            placeholder="Filter by slug (exact match)"
            data-testid="lp-analytics-slug-search"
            className="w-full border border-black pl-6 pr-6 py-1 text-sm"
          />
          {slugQ && (
            <button
              onClick={() => setSlugQ("")}
              data-testid="lp-analytics-slug-clear"
              className="absolute right-1 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-black"
              title="Clear"
            >
              <X size={10} weight="bold" />
            </button>
          )}
        </div>
      </div>

      {/* KPIs ------------------------------------------------------------- */}
      {totals && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="lp-analytics-kpis">
          <Kpi
            label="Impressions"
            value={num(totals.impressions)}
            sub={`${num(totals.unique_sessions)} unique sessions`}
            icon={Eye}
            testId="lp-kpi-impressions"
          />
          <Kpi
            label="CTA clicks"
            value={num(totals.cta_primary_click + totals.cta_phone_click)}
            sub={`Primary ${num(totals.cta_primary_click)} · Phone ${num(totals.cta_phone_click)}`}
            icon={CursorClick}
            testId="lp-kpi-cta"
          />
          <Kpi
            label="Form submits"
            value={num(totals.form_submit_success)}
            sub={`Attempts ${num(totals.form_submit_attempt)} · Starts ${num(totals.form_start)}`}
            icon={PaperPlaneTilt}
            testId="lp-kpi-submits"
          />
          <Kpi
            label="Conversion rate"
            value={pct(totals.conversion_rate)}
            sub={`CTA rate ${pct(totals.cta_click_rate)}`}
            icon={ChartLineUp}
            testId="lp-kpi-conversion"
          />
        </div>
      )}

      {/* Table ------------------------------------------------------------ */}
      <div className="brut-card overflow-x-auto" data-testid="lp-analytics-table-card">
        {loading && (
          <div className="p-8 text-center label-mono" data-testid="lp-analytics-loading">
            Loading…
          </div>
        )}
        {!loading && rows.length === 0 && (
          <div className="p-8 text-center label-mono" data-testid="lp-analytics-empty">
            No live traffic recorded in the selected window.
          </div>
        )}
        {!loading && rows.length > 0 && (
          <table className="w-full text-xs" data-testid="lp-analytics-table">
            <thead>
              <tr className="bg-black text-white uppercase tracking-widest">
                <th className="px-3 py-2 text-left">Slug</th>
                <th className="px-3 py-2 text-left">Kind</th>
                <th className="px-3 py-2 text-right">Impressions</th>
                <th className="px-3 py-2 text-right">Unique</th>
                <th className="px-3 py-2 text-right">CTA click</th>
                <th className="px-3 py-2 text-right">Form start</th>
                <th className="px-3 py-2 text-right">Submits</th>
                <th className="px-3 py-2 text-right">Conv.</th>
                <th className="px-3 py-2 text-left">Trend ({days}d)</th>
                <th className="px-3 py-2 text-left whitespace-nowrap">Last seen</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.page_key}
                  className="border-b border-zinc-200"
                  data-testid={`lp-analytics-row-${r.slug}`}
                >
                  <td className="px-3 py-2 font-mono">
                    <a
                      href={publicLink(`/lp/${r.slug}`)}
                      target="_blank" rel="noreferrer"
                      className="underline hover:text-red-700"
                      data-testid={`lp-analytics-open-${r.slug}`}
                    >
                      /lp/{r.slug}
                    </a>
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-block px-1.5 py-0.5 border text-[9px] uppercase tracking-widest ${
                        r.page_kind === "custom"
                          ? "bg-violet-50 border-violet-700 text-violet-900"
                          : "bg-zinc-50 border-zinc-500 text-zinc-700"
                      }`}
                    >{r.page_kind}</span>
                  </td>
                  <td className="px-3 py-2 text-right font-bold">{num(r.impressions)}</td>
                  <td className="px-3 py-2 text-right">{num(r.unique_sessions)}</td>
                  <td className="px-3 py-2 text-right">
                    {num(r.cta_primary_click + r.cta_phone_click)}
                  </td>
                  <td className="px-3 py-2 text-right">{num(r.form_start)}</td>
                  <td className="px-3 py-2 text-right">
                    <span className="font-bold text-emerald-800" data-testid={`lp-analytics-submits-${r.slug}`}>
                      {num(r.form_submit_success)}
                    </span>
                    <div className="text-[9px] text-zinc-500 leading-none">
                      {num(r.form_submit_attempt)} attempts
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right font-bold" data-testid={`lp-analytics-conv-${r.slug}`}>
                    {pct(r.conversion_rate)}
                  </td>
                  <td className="px-3 py-2">
                    <Sparkline series={r.daily} testId={`lp-analytics-spark-${r.slug}`} />
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-zinc-600">
                    {r.last_impression_at ? fmtDate(r.last_impression_at) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="text-[10px] text-zinc-500 mt-4 leading-relaxed max-w-3xl">
        <b>Impressions</b> are counted server-side on every /lp/&lt;slug&gt; hit
        via a first-party <span className="font-mono">navigator.sendBeacon</span> ping —
        no third-party tag, no cookies, no user-agent stored. Bot user-agents
        (Googlebot, curl, headless Chrome, etc.) are filtered.
        <b> Submits</b> are stamped by the intake pipeline only after the
        lead passes PI + duplicate + TCPA gates — the client cannot
        self-report a conversion.
      </div>
    </div>
  );
}
